import { z } from 'zod';
import { AdminApiError, adminFetch } from './api';

export const ACCOUNTING_PAGE_SIZE = 25;
export const ACCOUNTING_MAX_OFFSET = 1_000_000;
const requestId = z.string().regex(/^[A-Za-z0-9_-]{1,200}$/);
const units = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const tokens = z.number().int().min(0).max(2_147_483_647);
const usage = z.object({ inputTokens: tokens, outputTokens: tokens, cachedInputTokens: tokens })
  .refine((value) => value.cachedInputTokens <= value.inputTokens);

// Mirror recovery.ts's public allowlist; discard extra fields, including secrets.
const request = z.object({
  requestId,
  ownerUserId: z.string().min(1).max(200),
  modelId: z.string().nullable(),
  modelSlug: z.string().min(1).max(200).optional(),
  modelDisplayName: z.string().min(1).max(200).optional(),
  modelProvider: z.string().min(1).max(200).optional(),
  status: z.enum(['settlement_pending', 'settlement_failed', 'reconciliation_required', 'preparing', 'running']),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
  chargedUnits: units.nullable(),
  recordedUsage: usage.nullable(),
  proposedChargeUnits: units.nullable(),
  retryable: z.boolean(),
  stale: z.boolean(),
});
const page = z.object({
  requests: z.array(request).max(ACCOUNTING_PAGE_SIZE),
  limit: z.literal(ACCOUNTING_PAGE_SIZE),
  offset: z.number().int().min(0).max(ACCOUNTING_MAX_OFFSET),
  hasMore: z.boolean(),
  staleAfterSeconds: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
});
export type AccountingRequest = z.infer<typeof request>;
export type AccountingPage = z.infer<typeof page>;

export function canRetryAccounting(row: AccountingRequest): boolean {
  return row.retryable && row.recordedUsage !== null && row.proposedChargeUnits !== null
    && (row.status === 'settlement_pending' || row.status === 'settlement_failed');
}

export async function fetchAccounting(offset: number, signal: AbortSignal): Promise<AccountingPage> {
  const result = page.safeParse(await adminFetch<unknown>(`/admin/chat-accounting?limit=${ACCOUNTING_PAGE_SIZE}&offset=${offset}`, { signal }));
  if (!result.success || result.data.offset !== offset) throw new AdminApiError(502);
  return result.data;
}

export async function retryAccounting(row: AccountingRequest, signal: AbortSignal): Promise<void> {
  if (!canRetryAccounting(row) || !requestId.safeParse(row.requestId).success) throw new AdminApiError(409, 'ACCOUNTING_NOT_RETRYABLE');
  const result = z.object({ ok: z.literal(true), requestId }).safeParse(
    await adminFetch<unknown>(`/admin/chat-accounting/${encodeURIComponent(row.requestId)}/retry`, { method: 'POST', body: {}, signal }),
  );
  if (!result.success || result.data.requestId !== row.requestId) throw new AdminApiError(502);
}
